import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import styled, { css } from 'styled-components';
import { useWorkspacePermissions } from '@frameio/permissions';
import { useDispatchAppState } from '@frameio/shared/contexts';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useCreateProjectFromTemplate } from '@frameio/shared/resources/projects';
import type { CreatedProjectFromTemplateResult } from '@frameio/shared/resources/projects/hooks/useCreateProjectFromTemplate';
import { useLoadingToast } from '@frameio/shared/resources/shared/hooks';
import { getLink } from '@frameio/shared/routing';
import {
  Button2 as Button,
  Dialog,
  DialogHeader,
  Separator,
  theme,
  Tooltip,
  useActiveWindowBreakpoint,
} from '@frameio/vapor';
import { TemplatesList } from '../../project-templates';
import { useProjectTemplates } from '../../project-templates/hooks/useProjectTemplates';
import { useTemplateFieldValidation } from '../../project-templates/hooks/useTemplateFieldValidation';
import type { TemplateWithToggle } from '../../project-templates/TemplatesList';
import { WorkspaceSelect } from '../../shared/components/WorkspaceSelect';
import { useGetWorkspacesInfo } from '../../shared/hooks';
import TEXT from './generated/TEXT/CreateProjectModal';
import { ProjectAccessSwitch } from './ProjectAccessSwitch';
import { ProjectAvatarEditor } from './ProjectAvatarEditor';

TEXT.DEFINE({
  /** Dialog title and primary action label for creating a project */
  createNewProject: 'Create New Project',
  /** Default project name when none is provided */
  defaultProjectName: 'Untitled Project',
  /** Label for the avatar editor action */
  editAvatar: 'Edit project avatar',
  /** Error message when redirect to new project fails */
  unableToRedirect: "Can't redirect to project",
  /** Accessible label for the dialog close button */
  dialogCloseLabel: 'Close Dialog',
  /** Tooltip shown on the disabled Create button when no workspace is selected */
  selectWorkspaceTooltip: 'Select a workspace to create this project',
});

const DIALOG_HEIGHT = 600;
const DEFAULT_SAMPLE_BOOL = true;
const HEADER_HEIGHT = 48;
const WORKSPACE_INPUT_HEIGHT = 72;
const BASE_DIALOG_HEIGHT = 480;

const screenVariants = {
  base: {
    dialogWidth: '100%',
    dialogHeight: BASE_DIALOG_HEIGHT,
    pickerWidth: '100%',
  },
  tablet: {
    dialogWidth: 740,
    dialogHeight: BASE_DIALOG_HEIGHT,
    pickerWidth: 296,
  },
  desktop: {
    dialogWidth: 812,
    dialogHeight: BASE_DIALOG_HEIGHT,
    pickerWidth: 360,
  },
};

export type CreateProjectModalProps = {
  isShowing: boolean;
  onRequestClose: () => void;
  accountId: string;
  workspaceId: string;
  disableWorkspacePicker?: boolean;
};

export const CreateProjectModal = ({
  isShowing,
  onRequestClose,
  accountId,
  workspaceId,
  disableWorkspacePicker,
}: CreateProjectModalProps) => {
  const { data: currentWorkspacesData, previousData: previousWorkspacesData } =
    useGetWorkspacesInfo({
      accountId,
    });
  const workspaces =
    currentWorkspacesData?.account?.workspaces?.nodes ||
    previousWorkspacesData?.account?.workspaces?.nodes;

  const router = useRouter();
  const { addToast, updateAndHideToast } = useLoadingToast();
  const isWebClient = useIsMatchingClientName('web');

  const [isSavePending, setIsSavePending] = useState(false);

  const redirectToNewlyCreatedProject = (
    project: CreatedProjectFromTemplateResult
  ) => {
    if (project) {
      router.push(
        getLink({
          page: 'folder',
          projectId: project.id,
        })
      );
    } else {
      const toast = addToast(TEXT.createNewProject);
      updateAndHideToast(toast, {
        title: TEXT.unableToRedirect,
        variant: 'error',
      });
    }
  };

  const clearSavePendingState = () => {
    setIsSavePending(false);
  };

  const templates = useProjectTemplates();
  const createProject = useCreateProjectFromTemplate({
    onSuccess: redirectToNewlyCreatedProject,
    onError: clearSavePendingState,
  });

  const defaultTemplateIndex = 0;
  const [__selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const selectedTemplateId =
    __selectedTemplateId || templates?.[defaultTemplateIndex]?.id;
  const selectedTemplate = templates?.find((t) => t.id === selectedTemplateId);
  const dispatch = useDispatchAppState();
  const [name, setName] = useState(TEXT.defaultProjectName);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    workspaceId
  );
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const avatarThumbRef = useRef<string | null>(null);
  const avatarThumb = avatarThumbRef.current ?? selectedTemplate?.image;
  const [isRestricted, setIsRestricted] = useState(false);

  const handleToggleIsRestricted = useCallback(
    () => setIsRestricted((prev) => !prev),
    []
  );

  const [sampleAssetsToggleValues, setSampleAssetsToggleValues] = useState<{
    [key: string]: boolean;
  }>({});
  const breakpoint = useActiveWindowBreakpoint();

  const handleAvatarSave = useCallback((blob: Blob) => {
    setAvatarBlob(blob);
    avatarThumbRef.current = blob ? URL.createObjectURL(blob) : null;
  }, []);

  const shouldShowWorkspacePicker = workspaces?.length && workspaces.length > 1;
  const handleDialogClose = useCallback(() => {
    avatarThumbRef.current = null;
    onRequestClose();
  }, [onRequestClose]);

  const { defaultProjectName } = TEXT;

  const handleDialogExited = useCallback(() => {
    setName(defaultProjectName);
    setAvatarBlob(null);
    setSelectedTemplateId(null);
    setSelectedWorkspaceId(null);
    avatarThumbRef.current = null;
  }, [defaultProjectName]);

  const handleToggleSampleAsset = useCallback(
    (templateId: string, value: boolean) => {
      setSampleAssetsToggleValues((prevValues) => ({
        ...prevValues,
        [templateId]: value,
      }));
    },
    []
  );

  const shouldIncludeSampleAsset = useCallback(
    (templateId: string) => {
      return sampleAssetsToggleValues[templateId] ?? DEFAULT_SAMPLE_BOOL;
    },
    [sampleAssetsToggleValues]
  );

  // Get base validation data (no template fields)
  const { availableCustomFieldCount } = useTemplateFieldValidation();
  // Calculate validation per template
  const templatesWithToggleState = useMemo(() => {
    return (templates?.map((template) => {
      const templateFieldCount = template.customFieldCount || 0;
      const exceedsLimit = templateFieldCount > availableCustomFieldCount;
      return {
        ...template,
        shouldIncludeAssets: shouldIncludeSampleAsset(template.id),
        setShouldIncludeAssets: (value: boolean) =>
          handleToggleSampleAsset(template.id, value),
        fieldValidation: {
          exceedsLimit,
        },
      };
    }) || []) as TemplateWithToggle[];
  }, [
    templates,
    availableCustomFieldCount,
    shouldIncludeSampleAsset,
    handleToggleSampleAsset,
  ]);

  // Get selected template validation
  const selectedTemplateValidation = useMemo(() => {
    const selectedTemplateForValidation = templatesWithToggleState.find(
      (t) => t.id === selectedTemplateId
    );
    return (
      selectedTemplateForValidation?.fieldValidation ?? {
        exceedsLimit: false,
      }
    );
  }, [selectedTemplateId, templatesWithToggleState]);

  const canSave =
    !isSavePending &&
    name.trim() !== '' &&
    selectedWorkspaceId &&
    selectedTemplateId &&
    !selectedTemplateValidation.exceedsLimit;

  const handleSave = useCallback(() => {
    setIsSavePending(true);
    // Don't scroll if entering from NewProjectCard in workspace
    // and workspace was not changed
    if (!workspaceId || (workspaceId && workspaceId !== selectedWorkspaceId)) {
      dispatch({
        type: 'SCROLL_TO_NEW_PROJECT',
        payload: { workspaceId: selectedWorkspaceId! },
      });
    }
    if (!selectedTemplateId || !selectedWorkspaceId) return;
    const shouldIncludeAssets =
      sampleAssetsToggleValues[selectedTemplateId] ?? true;
    createProject(
      {
        destinationWorkspaceId: selectedWorkspaceId,
        name,
        templateId: selectedTemplateId,
        assetsToCopy: shouldIncludeAssets ? 'ALL' : 'FOLDERS_ONLY',
        private: isRestricted,
      },
      accountId,
      avatarBlob
    );
  }, [
    workspaceId,
    selectedWorkspaceId,
    selectedTemplateId,
    sampleAssetsToggleValues,
    createProject,
    name,
    isRestricted,
    accountId,
    avatarBlob,
    dispatch,
  ]);

  const handleNameKeyDown = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.key === 'Enter' && canSave) {
        handleSave();
      }
    },
    [handleSave, canSave]
  );

  useEffect(() => {
    if (!isShowing || selectedWorkspaceId) return;
    if (workspaceId) {
      setSelectedWorkspaceId(workspaceId);
    } else if (workspaces?.length === 1) {
      setSelectedWorkspaceId(workspaces[0].id);
    }
  }, [isShowing, selectedWorkspaceId, workspaceId, workspaces]);

  // For mobile, whether or not the header height
  // needs more space because of the input hint
  const getContentHeight = useCallback(() => {
    const baseHeight = screenVariants[breakpoint].dialogHeight;
    const workspaceHeight = shouldShowWorkspacePicker
      ? 0
      : WORKSPACE_INPUT_HEIGHT;
    return baseHeight - HEADER_HEIGHT - workspaceHeight;
  }, [breakpoint, shouldShowWorkspacePicker]);

  const canEditUserPermissions = useWorkspacePermissions({
    check: 'canEditUserPermissions',
    workspaceId: selectedWorkspaceId || workspaceId,
  });

  const ProjectSettings = (
    <ContentColumn
      $isWebClient={isWebClient}
      align="center"
      height={breakpoint !== 'base' ? BASE_DIALOG_HEIGHT : getContentHeight()}
      padding={breakpoint !== 'base' ? theme.spaces[24] : theme.spaces[0]}
    >
      <ProjectAvatarEditor
        avatarOriginal={avatarThumb}
        avatarThumb={avatarThumb}
        isRestricted={isRestricted}
        name={name}
        onNameChange={setName}
        onNameKeyDown={handleNameKeyDown}
        onSave={handleAvatarSave}
        shouldFocusOnMount={isShowing}
      />

      <ProjectAccessSwitch
        canManageAccess={canEditUserPermissions ?? false}
        isRestricted={isRestricted}
        onChange={handleToggleIsRestricted}
      />

      {shouldShowWorkspacePicker && !disableWorkspacePicker && (
        <WorkspaceSelect
          onSelect={setSelectedWorkspaceId}
          selectedWorkspaceId={selectedWorkspaceId ?? ''}
        />
      )}
    </ContentColumn>
  );

  return (
    <Dialog
      enableClickOutsideToClose={!isSavePending}
      isShowing={isShowing}
      onExited={handleDialogExited}
      onRequestClose={handleDialogClose}
      style={{
        height: DIALOG_HEIGHT,
        minWidth: screenVariants[breakpoint]?.dialogWidth,
        overflow: 'hidden',
      }}
    >
      <DialogHeader
        closeLabel={TEXT.dialogCloseLabel}
        showBottomBorder={false}
        showCloseButton={!isSavePending}
        title={TEXT.createNewProject}
      />
      <DialogBody
        $disableInteractions={isSavePending}
        $isWebClient={isWebClient}
      >
        {isWebClient ? (
          <>
            <ContentColumn
              $isWebClient={isWebClient}
              height={screenVariants[breakpoint].dialogHeight}
            >
              <TemplatesList
                hasWorkspacePicker={Boolean(shouldShowWorkspacePicker)}
                isMobile={breakpoint === 'base'}
                onTemplateSelect={setSelectedTemplateId}
                onToggleSampleAsset={handleToggleSampleAsset}
                selectedTemplateId={selectedTemplateId}
                shouldIncludeSampleAsset={shouldIncludeSampleAsset}
                templatesWithToggleState={templatesWithToggleState}
              >
                {breakpoint === 'base' && ProjectSettings}
              </TemplatesList>
            </ContentColumn>
            {breakpoint !== 'base' && (
              <>
                <Separator color="secondary" vertical={true} />
                {ProjectSettings}
              </>
            )}
          </>
        ) : (
          <>{ProjectSettings}</>
        )}
      </DialogBody>
      <FooterContainer>
        <Tooltip
          disabled={Boolean(selectedWorkspaceId)}
          placement="top"
          title={TEXT.selectWorkspaceTooltip}
        >
          <div>
            <Button
              data-testid="workspaces-create-project-modal--save-button"
              isDisabled={!canSave}
              onPress={handleSave}
              variant="primary"
            >
              {TEXT.createNewProject}
            </Button>
          </div>
        </Tooltip>
      </FooterContainer>
    </Dialog>
  );
};

const DialogBody = styled.div<{
  justify?: string;
  $disableInteractions?: boolean;
  $isWebClient?: boolean;
}>`
  align-items: center;
  border-bottom: 1px solid ${theme.colors.border.secondary};
  border-top: 1px solid ${theme.colors.border.secondary};
  display: flex;
  flex-direction: column-reverse;
  height: 480px;
  justify-content: start;

  ${(props) =>
    !props.$isWebClient &&
    css`
      justify-content: center;
      margin: 0 auto;
    `};

  ${(props) =>
    props.$disableInteractions &&
    css`
      pointer-events: none;
      user-select: none;

      > * {
        opacity: 0.25;
      }
    `}

  @media (min-width: ${theme.breakpoints.tablet}) {
    justify-content: ${(props) =>
      props.$isWebClient ? 'space-between' : 'center'};
    display: ${(props) => (props.$isWebClient ? 'grid' : 'flex')};
    grid-template-columns: ${(props) =>
      props.$isWebClient
        ? `1fr 1px ${screenVariants.desktop.pickerWidth}px`
        : undefined};
  }

  @media (min-width: ${theme.breakpoints.desktop}) {
    display: ${(props) => (props.$isWebClient ? 'grid' : 'flex')};
    justify-content: ${(props) =>
      props.$isWebClient ? 'space-between' : 'center'};
    grid-template-columns: ${(props) =>
      props.$isWebClient
        ? `1fr 1px ${screenVariants.desktop.pickerWidth}px`
        : undefined};
  }
`;

const ContentColumn = styled.div<{
  align?: string;
  height?: number;
  padding?: string;
  $isWebClient?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  width: 100%;

  ${({ padding }) => padding && `padding: ${padding}`};
  ${({ height }) => height && `height: ${height}px`};
  ${({ align }) => align && `align-items: ${align}`};

  ${(props) =>
    !props.$isWebClient &&
    css`
      padding: ${theme.spaces[16]};
      width: calc(100% - theme.spaces[32]);
    `};

  @media (min-width: ${theme.breakpoints.tablet}) {
    ${(props) =>
      !props.$isWebClient &&
      css`
        width: 100%;
      `}
  }
`;

const FooterContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row-reverse;
  height: ${theme.spaces[64]};
  padding: ${theme.spaces[16]};
`;
